import React from 'react';
import { XMarkIcon, ArrowTopRightOnSquareIcon, CalendarIcon } from '@heroicons/react/24/outline';

const AdvertisementPreviewModal = ({ 
  isOpen, 
  onClose, 
  advertisement 
}) => {
  if (!isOpen || !advertisement) return null;

  // Formatage des dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };


  // Obtenir le type MIME pour les fichiers
  const getFileMimeType = (banner) => {
    if (banner instanceof File) {
      return banner.type;
    }
    // Pour les chemins de fichier, deviner selon l'extension
    if (typeof banner === 'string') {
      const extension = banner.split('.').pop().toLowerCase();
      switch (extension) {
        case 'jpg':
        case 'jpeg':
          return 'image/jpeg';
        case 'png':
          return 'image/png';
        case 'mp4':
          return 'video/mp4';
        case 'mov':
          return 'video/quicktime';
        case 'avi':
          return 'video/x-msvideo';
        default:
          return advertisement.type === 'image' ? 'image/jpeg' : 'video/mp4';
      }
    }
    return '';
  };

  // Obtenir la taille du fichier
  const getFileSize = (banner) => {
    if (banner instanceof File) {
      const sizeInMB = (banner.size / (1024 * 1024)).toFixed(2);
      return `${sizeInMB} MB`;
    }
    return 'N/A';
  };


  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-800">
            Prévisualisation de la publicité
          </h2>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Fermer"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-6">
          {/* Statut */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className={`inline-flex rounded-full h-3 w-3 ${advertisement.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className="text-sm font-medium">
                {advertisement.is_active ? 'Publicité active' : 'Publicité inactive'}
              </span>
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <CalendarIcon className="h-4 w-4 mr-1" />
              <span>
                {formatDate(advertisement.start_date)} - {formatDate(advertisement.end_date)}
              </span>
            </div>
          </div>

          {/* Titre */}
          <h3 className="text-2xl font-bold text-gray-900">{advertisement.title}</h3>

          {/* Média */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 aspect-video flex items-center justify-center">
              {advertisement.type === 'image' ? (
                <img 
                  src={advertisement.banner_url}
                  alt={advertisement.title}
                  className="max-h-full max-w-full object-contain"
                />
              ) : (
                <video 
                  src={advertisement.banner_url}
                  controls
                  className="max-h-full max-w-full"
                />
              )}
            </div>
          </div>

          {/* Description */}
          {advertisement.description && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <p className="text-gray-600">{advertisement.description}</p>
            </div>
          )}

          {/* URL */}
          {advertisement.url && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Lien de destination</h4>
              <a 
                href={advertisement.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center text-primary hover:text-primary-dark underline"
              >
                {advertisement.url}
                <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
              </a>
            </div>
          )}

          {/* Détails techniques */}
          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Détails techniques</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Type</p>
                <p className="font-medium">{advertisement.type === 'image' ? 'Image' : 'Vidéo'}</p>
              </div>
              <div>
                <p className="text-gray-500">Taille du fichier</p>
                <p className="font-medium">{getFileSize(advertisement.banner)}</p>
              </div>
              <div>
                <p className="text-gray-500">Format</p>
                <p className="font-medium">
                  {getFileMimeType(advertisement.banner).split('/')[1].toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Chemin du fichier</p>
                <p className="font-medium text-xs truncate" title={typeof advertisement.banner === 'string' ? advertisement.banner : 'Fichier local'}>
                  {typeof advertisement.banner === 'string' ? advertisement.banner : 'Fichier local (non enregistré)'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvertisementPreviewModal;